import type { InferGetServerSidePropsType } from 'next';

import { Board } from '#components/Board/Board';
import { createDefaultGetServerSideProps } from '#cutils/page-context/server-side-props';
import { resolveBoard, type BoardData as BoardDataType } from '#domains/resolver/resolve-board';
import { envConfigServer } from '#sconfig/env.server';
import { logger } from '#utils/logging/logger';
import { extractSlugAndIdFromPathSegment } from '#utils/pathUtils';

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

export type BoardData = BoardDataType['board'];

function BoardPage({ board }: Props) {
	return <Board board={board} />;
}

export const getServerSideProps = createDefaultGetServerSideProps(async (ctx, apolloClient, withCacheTags) => {
	try {
		let id: string | undefined;
		let slug: string | undefined;

		// the catch all route will return an array https://nextjs.org/docs/pages/building-your-application/routing/dynamic-routes#optional-catch-all-segments
		if (!ctx.params?.board || ctx.params?.board?.length === 0) {
			id = envConfigServer.HOMEPAGE_BOARD_ROW_ID;
			slug = '';
		} else if (ctx.params?.board?.length === 1) {
			const parsedPath = extractSlugAndIdFromPathSegment(ctx.params.board[0]);
			id = parsedPath.id;
			slug = parsedPath.slug;
		} else {
			// if we have more than 1 path segment there is no match for this url
			return { notFound: true };
		}

		const resolutionResult = await resolveBoard(apolloClient, id, slug, withCacheTags, ctx.resolvedUrl);

		if ('redirect' in resolutionResult || 'notFound' in resolutionResult) {
			return resolutionResult;
		}

		return { props: resolutionResult };
	} catch (err: any) {
		logger.error(err, `Error resolving data for board`);
		throw err;
	}
});

export default BoardPage;
